import { inject, Injectable, WritableSignal } from '@angular/core';
import { Store } from '@ngxs/store';
import { Dialog } from '@angular/cdk/dialog';
import { TsSnackbarService } from 'tsui';
import { Router } from '@angular/router';
import { ConfirmationDialogService } from '@shared/components/widgets/dialog/confirmation/confirmation-dialog.service';
import { RelatedToType } from '@shared/enums';
import { QueryConfig, SavedView } from '@core/models';
import { TsMenuItem } from 'tsui/lib/ts-menu/ts-menu.component';
import { catchError, filter, finalize, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { SavedViewsActions } from '@state/saved-view/action';
import { SavedViewsState } from '@state/saved-view/state';
import { SavedViewEditDialogComponent } from '@shared/components/saved-view-edit-dialog/saved-view-edit-dialog.component';
import { TsDialogComponent } from 'tsui/lib/ts-dialog/ts-dialog.component';
import { EMPTY, map, noop } from 'rxjs';
import { AccountService } from '@core/services';
import { PermissionUtils } from '@core/utils/permission.utils';
import { SavedViewsSidebarService } from '@shared/services/saved-views-sidebar.service';

type ExtendedRelatedToType = RelatedToType | `co_${string}` | string;

/**
 * We can progressively extract common functionality on all Saved Views tables and add them here.
 * (e.g.: Properties, Companies, Contacts and Custom Objects)
 */
@Injectable({
  providedIn: 'root',
})
export class SavedViewUtilsService {
  readonly store = inject(Store);
  readonly dialog = inject(Dialog);
  readonly snackBar = inject(TsSnackbarService);
  readonly router = inject(Router);
  readonly confirmationService = inject(ConfirmationDialogService);
  readonly accountService = inject(AccountService);
  readonly savedViewsSidebarService = inject(SavedViewsSidebarService);

  getViewManagementMenuOptions(
    savedView: WritableSignal<SavedView>,
    relatedToType: ExtendedRelatedToType,
    loading?: WritableSignal<boolean>,
    customObjectId?: number,
  ): TsMenuItem[] {
    const rootRedirectPath = customObjectId
      ? `objects/${customObjectId}`
      : SavedViewUtilsService.redirectRootPaths[relatedToType];
    const editOption = PermissionUtils.canEdit(savedView()) ? this.getEditOption(savedView) : null;
    const duplicateOption = this.savedViewsSidebarService.canAddSavedViewByType(<RelatedToType>relatedToType)
      ? this.getDuplicateOption(savedView, relatedToType, rootRedirectPath)
      : null;
    const viewCount = this.store
      .selectSnapshot(SavedViewsState.getViews)
      ?.filter((x) => x.related_to_type === relatedToType)?.length;
    const deleteOption =
      PermissionUtils.canDelete(savedView()) && viewCount > 1
        ? this.getDeleteOption(savedView, relatedToType, rootRedirectPath, loading)
        : null;

    return [editOption, duplicateOption, deleteOption].filter((option) => !!option);
  }

  saveTableSort(savedView: WritableSignal<SavedView>, sortState: { active: string; direction: string }) {
    const sortDefinition = { active: sortState.active, direction: sortState.direction };

    savedView.update((sv) => {
      return {
        ...sv,
        definition: JSON.stringify({
          table: {
            fields: JSON.parse(sv.definition).table?.fields,
            sort: sortDefinition,
          },
        }),
      };
    });

    this.accountService.updateSavedView(savedView()).pipe(take(1)).subscribe(noop);
  }

  saveFilter(filters: Partial<QueryConfig.QueryFilters>, savedView: WritableSignal<SavedView>) {
    if (savedView()) {
      savedView.update((sv) => {
        return {
          ...sv,
          saved_filter: {
            ...sv.saved_filter,
            filters: JSON.stringify(filters),
          },
        };
      });

      if (!savedView().saved_filter.id) {
        this.accountService
          .saveFilter(savedView().saved_filter)
          .pipe(take(1))
          .subscribe((savedFilter) => {
            let url = this.router.url.split('?')[0];
            this.router.navigate([url], { queryParams: { filterId: savedFilter.id } });
          });
      } else {
        this.accountService.updateFilter(savedView().saved_filter).pipe(take(1)).subscribe(noop);
      }
    }
  }

  private static get redirectRootPaths(): { [key: string]: string } {
    return {
      [RelatedToType.Property]: 'properties',
      [RelatedToType.Company]: 'companies',
      [RelatedToType.Contact]: 'contacts',
    };
  }

  private getDeleteOption(
    savedView: WritableSignal<SavedView>,
    relatedToType: ExtendedRelatedToType,
    rootRedirectPath: string,
    loading?: WritableSignal<boolean>,
  ) {
    return {
      label: 'Delete View',
      icon: 'ts_trash-alt',
      variant: 'danger',
      action: () => {
        loading?.set(true);

        this.confirmationService
          .openConfirmationDialog(ConfirmationDialogService.deleteConfirmationCommonLayoutConfig())
          .pipe(
            filter((result) => {
              if (result) {
                return true;
              }

              loading?.set(false);
              return false;
            }),
            switchMap(() => {
              return this.store.dispatch(new SavedViewsActions.DeleteView(savedView().id));
            }),
            withLatestFrom(this.store.select(SavedViewsState.getViews)),
            take(1),
            finalize(() => {
              loading?.set(false);
            }),
          )
          .subscribe(([_, savedViews]) => {
            if (!SavedViewUtilsService.redirectRootPaths[relatedToType] && !relatedToType?.startsWith('co_')) {
              console.error('No redirect path found for relatedToType:', relatedToType);
            } else {
              const firstSavedViewId = this.store.selectSnapshot(
                SavedViewsState.getAllowedSortedRelatedToTypeViews(relatedToType),
              )[0]?.id;
              const redirectRouteSuffix = firstSavedViewId ? [firstSavedViewId, 'table'] : [];

              this.router.navigate([rootRedirectPath, ...redirectRouteSuffix]);
            }
          });
      },
    };
  }

  private getEditOption(savedView: WritableSignal<SavedView>) {
    return {
      label: 'Edit View',
      icon: 'ts_pen',
      action: () => {
        this.dialog
          .open(SavedViewEditDialogComponent, {
            data: { ...savedView() },
            ...TsDialogComponent.getDefaultDialogConfig(),
          })
          .closed.pipe(
            filter((result) => !!result),
            take(1),
          )
          .subscribe((changedSavedView: SavedView) => {
            savedView.set(changedSavedView);
          });
      },
    };
  }

  private getDuplicateOption(
    savedView: WritableSignal<SavedView>,
    relatedToType: ExtendedRelatedToType,
    rootRedirectPath: string,
  ) {
    return {
      label: 'Duplicate',
      icon: 'ts_copy',
      action: () => {
        const newViewName = `${savedView().name} (Copy)`;

        this.store
          .dispatch(
            new SavedViewsActions.AddView({
              ...savedView(),
              id: null,
              created_at: null,
              name: newViewName,
            }),
          )
          .pipe(
            catchError((err) => {
              this.snackBar.open(err?.error?.error || 'Unable to duplicate view', 'error');
              return EMPTY;
            }),
            withLatestFrom(this.store.select(SavedViewsState.getViews)),
            map(([, views]) => views.find((view) => view.name === newViewName)?.id),
            filter((newViewId) => !!newViewId),
            take(1),
          )
          .subscribe((newViewId) => {
            if (!SavedViewUtilsService.redirectRootPaths[relatedToType] && !relatedToType?.startsWith('co_')) {
              console.error('No redirect path found for relatedToType:', relatedToType);
            } else {
              this.router.navigate([rootRedirectPath, +newViewId, 'table']);
            }
          });
      },
    };
  }
}
